/**
 * One-click "open the PDF in a new tab" control for a Bank Transactions
 * Reconciled By row whose settling invoice's PDF availability was already
 * resolved server-side (reconciled_by.pdf_source, routes/transactions.py::
 * _batch_reconciled_by / _resolve_invoice_pdf_source -- mirrors
 * detectPdfSource in lib/invoicePdfSource.js). The caller only mounts this
 * when pdf_source is already known truthy, so there is no disabled state
 * here -- that's InvoicePdfButton's job everywhere else in the app.
 *
 * Fetches through the authenticated API client (axios with Authorization
 * header, responseType: 'blob') and opens the resulting blob object URL
 * with window.open, same established pattern as ReconciliationDrilldown.jsx
 * and ReconTracePanel.jsx -- never a raw <a href> to the API endpoint
 * (the browser does not attach the bearer token to a direct navigation,
 * see components/__tests__/invoicePdfAuthGuard.test.js).
 */

import { useState, useCallback } from 'react';
import { FileText, Loader2 } from 'lucide-react';
import { invoicesAPI } from '../lib/api';
import { cn } from '../lib/utils';
import { toast } from 'sonner';
import { getErrorMessage } from '../lib/errors';

export function InvoicePdfNewTabLink({ invoiceId, pdfSource, className }) {
  const [loading, setLoading] = useState(false);

  const handleClick = useCallback(async (e) => {
    e.stopPropagation();
    e.preventDefault();
    setLoading(true);
    try {
      const fetcher = pdfSource === 'gmail' ? invoicesAPI.fetchAttachment : invoicesAPI.fetchPdf;
      const res = await fetcher(invoiceId);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (err) {
      toast.error(getErrorMessage(err) || 'Failed to open PDF');
    } finally {
      setLoading(false);
    }
  }, [invoiceId, pdfSource]);

  return (
    <button
      onClick={handleClick}
      disabled={loading}
      className={cn('inline-flex items-center gap-1 text-muted-foreground hover:text-primary', className)}
      title="Open PDF"
      data-testid="invoice-pdf-new-tab"
    >
      {loading
        ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
        : <FileText className="h-3.5 w-3.5" />}
    </button>
  );
}
